/**
 * CONFIRM MODAL - Componente Genérico
 * Design: Things 3 / OmniFocus - Quiet Luxury
 * Cores Bigwa: Verde #477B72, Amarelo #F2BC41, Coral #D4918A
 * 
 * COMPATÍVEL com uso existente no HomeInspector
 */

import React from 'react';
import './ConfirmModal.css';
import { ArchiveIcon, CloseIcon, DeleteIcon, IncompleteIcon, InfoIcon } from './bigwa-icons';
import { useDelayedUnmount } from '../hooks/useDelayedUnmount';

type ModalVariant = 'danger' | 'warning' | 'info';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message?: string;
  // Suporta ambos os nomes para compatibilidade
  confirmLabel?: string;
  confirmText?: string;
  cancelLabel?: string;
  cancelText?: string;
  variant?: ModalVariant;
  icon?: string;
  onConfirm: () => void;
  // Suporta ambos: onCancel e onClose
  onCancel?: () => void;
  onClose?: () => void;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmText,
  cancelLabel,
  cancelText,
  variant = 'danger',
  icon,
  onConfirm,
  onCancel,
  onClose,
}: ConfirmModalProps) {
  const { shouldRender, isAnimating, onAnimationEnd } = useDelayedUnmount(isOpen);

  if (!shouldRender) return null;

  // Usa onClose se onCancel não foi fornecido
  const handleClose = onCancel || onClose || (() => {});
  
  // Usa confirmText se confirmLabel não foi fornecido
  const confirmButtonText = confirmLabel || confirmText || 'Confirmar';
  const cancelButtonText = cancelLabel || cancelText || 'Cancelar';

  const getIcon = () => {
    // Se icon foi passado como string, mapeia
    if (icon === 'archive') return <ArchiveIcon size={24} />;
    if (icon === 'trash') return <DeleteIcon size={24} />;
    if (icon === 'warning') return <IncompleteIcon size={24} />;
    if (icon === 'info') return <InfoIcon size={24} />;
    
    // Senão, usa baseado no variant
    switch (variant) {
      case 'danger':
        return <DeleteIcon size={24} />;
      case 'warning':
        return <IncompleteIcon size={24} />;
      case 'info':
        return <InfoIcon size={24} />;
      default:
        return <IncompleteIcon size={24} />;
    }
  };

  return (
    <div className={`confirm-modal-backdrop ${isAnimating ? 'overlay-animate-out' : 'overlay-animate-in'}`} onClick={handleClose}>
      <div className={`confirm-modal ${isAnimating ? 'modal-animate-out' : 'modal-animate-in'}`} onClick={e => e.stopPropagation()} onAnimationEnd={onAnimationEnd}>
        {/* Header */}
        <div className="confirm-modal__header">
          <div className={`confirm-modal__icon confirm-modal__icon--${variant}`}>
            {getIcon()}
          </div>
          <button className="confirm-modal__close" onClick={handleClose}>
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="confirm-modal__content">
          <h2 className="confirm-modal__title">{title}</h2>
          {message && <p className="confirm-modal__message">{message}</p>}
        </div>

        {/* Actions */}
        <div className="confirm-modal__actions">
          <button 
            className="confirm-modal__btn confirm-modal__btn--cancel"
            onClick={handleClose}
          >
            {cancelButtonText}
          </button>
          <button 
            className={`confirm-modal__btn confirm-modal__btn--confirm confirm-modal__btn--${variant}`}
            onClick={onConfirm}
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
